import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  PageType,
  QueryKeys,
  mutationHandler,
  useAppLocation,
  useAppState,
  useAppTranslation,
} from "../../../common";
import { ErrorModal, Modal } from "../../../components";
import { deleteLineOfPoetryAction } from "../../../actions";
import { LinesOfPoetriesEntity } from "../../../entity";

type LineOfPoetryDeleteModalProps = {
  handleDeleteModalVisible: () => void;
  handleSetPage: (value: number) => void;
};

export function LineOfPoetryDeleteModal(props: LineOfPoetryDeleteModalProps) {
  const { t } = useAppTranslation();
  const location = useAppLocation();

  const queryClient = useQueryClient();
  const [state, setState] = useAppState({
    errorModalVisible: false,
  });

  const handleModalVisibility = () => {
    setState((prevState) => ({
      ...prevState,
      errorModalVisible: !prevState.errorModalVisible,
    }));
  };

  const deleteLine = useMutation({
    mutationKey: [QueryKeys.deleteLineOfPoetry],
    mutationFn: deleteLineOfPoetryAction,
    onSuccess() {
      props.handleDeleteModalVisible();
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.linesOfPoetryQuery],
      });
      let page = 1;

      queryClient.setQueryData(
        [QueryKeys.linesOfPoetryQuery],
        (old: PageType<LinesOfPoetriesEntity>) => {
          const handler = mutationHandler({
            dataLength: old.data.length,
            pageSize: old.pageSize,
            count: old.count,
          });
          page = handler.page;

          return {
            page: old.page,
            pageSize: old.pageSize,
            count: handler.page,
            data: old.data,
          };
        }
      );
      props.handleSetPage(page);
    },
    onError() {
      handleModalVisibility();
    },
  });

  const handleDeleteLine = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    deleteLine.mutate({ id: location.state?.id });
  };

  return (
    <div>
      <section>
        <Modal
          onClose={props.handleDeleteModalVisible}
          title={t("line-of-poetry-page-delete-modal-title")}
          footer
          submitText={t("line-of-poetry-page-delete-submit-button-title")}
          cancelText={t("line-of-poetry-page-delete-cancel-button-title")}
          handleSubmit={handleDeleteLine}
          loading={deleteLine.isPending}
        >
          <span>{t("line-of-poetry-page-delete-line-question-label")}</span>
        </Modal>
      </section>
      <section>
        {state.errorModalVisible && (
          <ErrorModal
            error={deleteLine.error}
            onClose={handleModalVisibility}
            title={t("line-of-poetry-page-delete-poet-error-modal-title")}
          />
        )}
      </section>
    </div>
  );
}
